import { LoaderCircle, Search, SlidersHorizontal, X } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";

type SearchComposerProps = {
  initialQuery: string;
  isSearching: boolean;
  canClear: boolean;
  advancedOpen: boolean;
  showControls?: boolean;
  threshold: number;
  maxSources: number;
  onAdvancedToggle: () => void;
  onThresholdChange: (threshold: number) => void;
  onMaxSourcesChange: (maxSources: number) => void;
  onSearch: (query: string) => void;
  onClear: () => void;
};

export function SearchComposer({
  initialQuery,
  isSearching,
  canClear,
  advancedOpen,
  showControls = true,
  threshold,
  maxSources,
  onAdvancedToggle,
  onThresholdChange,
  onMaxSourcesChange,
  onSearch,
  onClear,
}: SearchComposerProps) {
  const [draft, setDraft] = useState(initialQuery);

  useEffect(() => {
    setDraft(initialQuery);
  }, [initialQuery]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSearch(draft.trim());
  }

  function handleClear() {
    setDraft("");
    onClear();
  }

  const clearDisabled = !canClear && draft.trim().length === 0 && !isSearching;

  return (
    <form className="search-composer" onSubmit={handleSubmit}>
      <div className="search-composer__input">
        <Search size={20} aria-hidden="true" />
        <input
          aria-label="Search documents"
          placeholder="Search documents, circulars, gazettes, templates"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      </div>
      <button className="primary-button" type="submit" disabled={isSearching}>
        {isSearching ? (
          <LoaderCircle className="button-spinner" size={18} aria-hidden="true" />
        ) : (
          <SlidersHorizontal size={18} aria-hidden="true" />
        )}
        <span>{isSearching ? "Searching" : "Find sources"}</span>
      </button>
      <button
        className="secondary-button search-composer__clear"
        type="button"
        disabled={clearDisabled}
        onClick={handleClear}
      >
        <X size={18} aria-hidden="true" />
        <span>Clear</span>
      </button>
      <button
        className="secondary-button search-composer__advanced"
        type="button"
        aria-expanded={advancedOpen}
        onClick={onAdvancedToggle}
      >
        <SlidersHorizontal size={18} aria-hidden="true" />
        <span>{advancedOpen ? "Hide advanced" : "Advanced"}</span>
      </button>
      {showControls && advancedOpen ? (
        <SearchSettingsControls
          className="search-composer__controls"
          threshold={threshold}
          maxSources={maxSources}
          onThresholdChange={onThresholdChange}
          onMaxSourcesChange={onMaxSourcesChange}
        />
      ) : null}
    </form>
  );
}

type SearchSettingsControlsProps = {
  className?: string;
  threshold: number;
  maxSources: number;
  onThresholdChange: (threshold: number) => void;
  onMaxSourcesChange: (maxSources: number) => void;
};

export function SearchSettingsControls({
  className,
  threshold,
  maxSources,
  onThresholdChange,
  onMaxSourcesChange,
}: SearchSettingsControlsProps) {
  return (
    <div className={["search-settings", className].filter(Boolean).join(" ")}>
      <label>
        <span>Relevance threshold {threshold.toFixed(2)}</span>
        <input
          aria-label="Relevance threshold"
          type="range"
          min="0.1"
          max="0.9"
          step="0.05"
          value={threshold}
          onChange={(event) => onThresholdChange(Number(event.target.value))}
        />
      </label>
      <label>
        <span>Result sources {maxSources}</span>
        <input
          aria-label="Maximum sources"
          type="range"
          min="1"
          max="50"
          step="1"
          value={maxSources}
          onChange={(event) => onMaxSourcesChange(Number(event.target.value))}
        />
      </label>
    </div>
  );
}
